import * as React from 'react';

import { FormControl, FormHelperText, MenuItem, Select, Typography } from '@mui/material';
import type { SxProps } from '@mui/material';
import { red50, gray100, gray80, gray40 } from '../../../constants/colors';
import { formLabelWrapper, menuItemBackgroundStyle } from '../../../constants/location';

import type { FieldError } from 'react-hook-form';

type selectProps = React.ComponentProps<typeof Select>;

export interface ListType {
  disabled?: boolean;
  option: string;
  value: number | string;
  description?: string;
  // この項目の下に区切り線を引く（選択肢のグループ分け用）
  divider?: boolean;
}

type Props = selectProps & {
  disabled?: boolean;
  error?: boolean;
  formMb?: string;
  helperText?: string | FieldError | undefined;
  labelText?: string;
  list: ListType[];
  requiredText?: string;
  placeholder?: string | number;
  type?: string;
  setMaterialRequestModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  height?: string;
  width?: string;
  spWidth?: string;
  multiple?: boolean;
  applyMultiline?: boolean;
  value?: string | number | (string | number)[];
  borderColor?: string;
  fontSize?: string;
};

const CustomSelectField = React.forwardRef((props: Props, ref): React.ReactElement => {
  const {
    disabled,
    error,
    formMb,
    helperText,
    labelText,
    list,
    placeholder,
    requiredText,
    type,
    height,
    width,
    spWidth,
    multiple,
    applyMultiline,
    value,
    borderColor,
    fontSize,
    ...rest
  } = props;

  function inputBorderColorStyle(): SxProps | undefined {
    if (error) {
      return {
        '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${red50}`,
        },
      };
    }
    if (borderColor) {
      return {
        '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${borderColor}`,
        },
        '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${gray100}`,
        },
      };
    }
    return {
      '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${gray100}`,
      },
    };
  }

  const valueSwitcher = (): (string | number)[] => {
    if (multiple) {
      if (Array.isArray(value)) {
        return value;
      } else {
        return [0];
      }
    } else {
      if (typeof value === 'string' || typeof value === 'number') {
        return [value];
      } else {
        return [0];
      }
    }
  };

  const selectedValues = valueSwitcher();

  const styles = {
    formMb: { marginBottom: formMb ?? '0' },
    formLabelWrapper,
    label: {
      color: gray80,
      fontSize: '14px',
    },
    required: {
      color: red50,
      fontSize: '14px',
      marginLeft: '8px',
    },
    select: {
      '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${gray100}`,
      },
      '&.Mui-disabled': {
        '& .MuiSelect-select': {
          WebkitTextFillColor: `${gray80} !important`,
          color: `${gray80} !important`,
        },
        bgcolor: 'rgba(0, 0, 0, 0.06)',
      },
      bgcolor: '#FFF',
      height: applyMultiline && selectedValues?.length > 1 ? undefined : (height ?? '51px'),
      minHeight: applyMultiline && selectedValues?.length > 1 ? (height ?? '51px') : undefined,
      ...inputBorderColorStyle(),
      width: width ?? '100%',
      '@media screen and (max-width:600px)': {
        width: spWidth ?? '100%',
        height: applyMultiline && selectedValues?.length > 1 ? undefined : '40px',
        minHeight: applyMultiline && selectedValues?.length > 1 ? '40px' : undefined,
      },
    },
  };

  const textfieldHelper = (): string | undefined => {
    if (helperText === undefined) return;
    return String(helperText);
  };

  const optionsMap: { [key: string]: string } = {};
  list.forEach(item => {
    optionsMap[item.value] = item.option;
  });

  // リストに value: 0 の項目が存在するかチェック（0が有効な選択肢かどうか）
  const hasZeroAsValidOption = list.some(item => item.value === 0);

  return (
    <Typography align="left" component="div" sx={styles.formMb}>
      {labelText && (
        <Typography align="left" sx={styles.formLabelWrapper} component="div">
          <Typography sx={styles.label} component="span">
            {labelText}
          </Typography>
          {requiredText && (
            <Typography component="span" sx={styles.required}>
              {requiredText}
            </Typography>
          )}
        </Typography>
      )}
      <Typography sx={{ textAlign: 'left' }} component="div">
        <FormControl sx={{ width: '100%' }} {...(error && { error: true })}>
          <Select
            {...(disabled && { disabled })}
            {...(multiple && { multiple })}
            {...rest}
            ref={ref}
            sx={styles.select}
            displayEmpty
            value={multiple ? valueSwitcher() : valueSwitcher()[0]}
            renderValue={(selected): React.ReactNode => {
              const isPlaceholder =
                multiple && Array.isArray(selected)
                  ? selected.length === 1 && selected[0] === 0 && !hasZeroAsValidOption
                  : (selected === 0 && !hasZeroAsValidOption) ||
                    selected === '選択してください' ||
                    selected === '' ||
                    selected === '__placeholder__';
              if (isPlaceholder) {
                return (
                  <Typography
                    component="p"
                    sx={{
                      color: gray80,
                      fontSize: fontSize ?? '14px',
                      fontWeight: 'unset',
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {placeholder ?? '選択してください'}
                  </Typography>
                );
              }
              if (multiple && Array.isArray(selected) && selected.length > 0) {
                return (
                  <Typography
                    component="p"
                    sx={{
                      color: gray100,
                      fontSize: fontSize ?? '14px',
                      fontWeight: type === 'primary' ? 500 : 'unset',
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {selected
                      .filter(value => hasZeroAsValidOption || value !== 0)
                      .map(value => optionsMap[String(value)])
                      .join(', ')}
                  </Typography>
                );
              }
              return (
                <Typography
                  component="p"
                  sx={{
                    color: gray100,
                    fontSize: fontSize ?? '14px',
                    fontWeight: type === 'primary' ? 500 : 'unset',
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {optionsMap[String(selected)]}
                </Typography>
              );
            }}
          >
            {placeholder && (
              <MenuItem value="__placeholder__" sx={{ color: gray80, ...menuItemBackgroundStyle }}>
                <Typography
                  component="p"
                  sx={{
                    color: gray80,
                    fontSize: '14px',
                    fontWeight: 'unset',
                    textAlign: 'left',
                    whiteSpace: 'break-spaces',
                  }}
                >
                  {placeholder ?? '選択してください'}
                </Typography>
              </MenuItem>
            )}
            {list.map(item => (
              <MenuItem
                key={item.value}
                value={item.value}
                sx={{
                  color: gray100,
                  whiteSpace: 'normal',
                  ...menuItemBackgroundStyle,
                  ...(item.divider && { borderBottom: `1px solid ${gray40}` }),
                }}
                {...(item.disabled && { disabled: item.disabled })}
              >
                {item.description ? (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      component="span"
                      sx={{
                        color: gray100,
                        fontSize: '14px',
                        fontWeight: type === 'primary' ? 500 : 'unset',
                        textAlign: 'left',
                      }}
                    >
                      {item.option}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        color: gray80,
                        fontSize: '12px',
                        textAlign: 'left',
                      }}
                    >
                      {item.description}
                    </Typography>
                  </div>
                ) : (
                  <Typography
                    component="p"
                    sx={{
                      color: gray100,
                      fontSize: '14px',
                      fontWeight: type === 'primary' ? 500 : 'unset',
                      textAlign: 'left',
                      whiteSpace: 'break-spaces',
                    }}
                  >
                    {item.option}
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Typography>
      {error && <FormHelperText error={error}>{textfieldHelper()}</FormHelperText>}
    </Typography>
  );
});

CustomSelectField.displayName = 'CustomSelectField';
export default CustomSelectField;
